/**
 * Provision form — deploy fields + schema-driven setup on one pane.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { CreateInstanceForm } from '@/components/ai-studio/instances';
import { InstanceSetupReviewStep } from '@/components/ai-studio/instance-setup/InstanceSetupReviewStep';
import { InstanceSetupSchemaForm } from '@/components/ai-studio/instance-setup/InstanceSetupSchemaForm';
import { useInstanceSetupWizard } from '@/components/ai-studio/instance-setup/useInstanceSetupWizard';
import { createInstance } from '@/services/aiStudioAgentService';
import { saveInstanceSetup } from '@/services/aiStudioInstanceSetupService';
import type { CreateInstanceRequest, InstanceDeployPatch, InstanceProvisionPatch } from '@/types/aiStudioAgent';
import type { ProvisionAssistDraft } from './useProvisionAssist';
import type { SetupWizardStep } from '@/components/ai-studio/instance-setup/types';

const SECTION_CLASS =
  'rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-3';

interface ProvisionFormPanelProps {
  agentId: string;
  instanceId: string | null;
  suggestedPatch: InstanceProvisionPatch | null;
  onSuggestionApplied: () => void;
  onDraftSnapshot: (draft: ProvisionAssistDraft) => void;
  onInstanceCreated: (instanceId: string) => void;
}

export const ProvisionFormPanel: React.FC<ProvisionFormPanelProps> = ({
  agentId,
  instanceId,
  suggestedPatch,
  onSuggestionApplied,
  onDraftSnapshot,
  onInstanceCreated,
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [createErrorKey, setCreateErrorKey] = useState<string | null>(null);
  const [deployPatch, setDeployPatch] = useState<InstanceDeployPatch | null>(null);

  const wizard = useInstanceSetupWizard(instanceId ?? undefined);

  useEffect(() => {
    if (!suggestedPatch) return;
    if (suggestedPatch.deploy) {
      setDeployPatch(suggestedPatch.deploy);
    }
    if (suggestedPatch.setup && instanceId) {
      if (suggestedPatch.setup.site?.name) {
        wizard.setFieldValue('instance_display_name', suggestedPatch.setup.site.name);
      }
      if (suggestedPatch.setup.site?.location) {
        wizard.setFieldValue('instance_location', suggestedPatch.setup.site.location);
      }
      void saveInstanceSetup(instanceId, {
        site: {
          name: suggestedPatch.setup.site?.name ?? wizard.site.name,
          location: suggestedPatch.setup.site?.location ?? wizard.site.location,
        },
        fields: suggestedPatch.setup.fields,
        setupNotes: suggestedPatch.setup.setup_notes,
      }).catch(() => undefined);
    }
    if (suggestedPatch.markSetupComplete && instanceId) {
      void saveInstanceSetup(instanceId, {
        fields: wizard.fieldValues,
        markComplete: true,
      }).then(() => wizard.goToInstance());
    }
    onSuggestionApplied();
  }, [suggestedPatch]); // eslint-disable-line react-hooks/exhaustive-deps -- apply once per suggestion

  const handleCreate = useCallback(
    async (request: CreateInstanceRequest) => {
      setCreating(true);
      setCreateErrorKey(null);
      try {
        const instance = await createInstance(request);
        onInstanceCreated(instance.id);
      } catch {
        setCreateErrorKey('aiStudio.instances.createFailed');
      } finally {
        setCreating(false);
      }
    },
    [onInstanceCreated]
  );

  const setupSteps: SetupWizardStep[] = useMemo(() => ['form', 'review'], []);

  const stepLabel = (step: SetupWizardStep) => {
    if (step === 'form') return t('aiStudio.instanceSetup.stepForm');
    return t('aiStudio.instanceSetup.stepReview');
  };

  const provisionFields = wizard.schema?.provisionFields ?? [];

  if (!instanceId) {
    return (
      <div className="flex flex-col gap-3">
        {createErrorKey && (
          <p role="alert" className="text-base text-[var(--color-error)]">
            {t(createErrorKey)}
          </p>
        )}
        <section className={SECTION_CLASS}>
          <h3 className="mb-2 text-base font-semibold text-[var(--color-surface-text)]">
            {t('aiStudio.instances.deployInstance')}
          </h3>
          <CreateInstanceForm
            embedded
            submitting={creating}
            initialAgentId={agentId}
            suggestedPatch={deployPatch}
            onSuggestionApplied={() => setDeployPatch(null)}
            onDraftSnapshot={(draft) =>
              onDraftSnapshot({
                ...draft,
                agentId,
                instanceId: instanceId ?? undefined,
              })
            }
            submitLabelKey="aiStudio.provision.createInstance"
            onSubmit={(request) => void handleCreate(request)}
            onCancel={() => navigate('/ai-studio/instances')}
          />
        </section>
      </div>
    );
  }

  if (wizard.loading) {
    return (
      <p className="text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.instanceSetup.loading')}
      </p>
    );
  }

  if (!wizard.status) {
    return (
      <p role="alert" className="text-base text-[var(--color-error)]">
        {t(wizard.errorKey ?? 'aiStudio.instanceSetup.notFound')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <section className={SECTION_CLASS}>
        <h3 className="mb-1 text-base font-semibold text-[var(--color-surface-text)]">
          {t('aiStudio.instanceSetup.title')}
        </h3>
        <nav aria-label={t('aiStudio.instanceSetup.title')} className="flex flex-wrap gap-2">
          {setupSteps.map((step, index) => {
            const active = wizard.step === step;
            const done =
              (step === 'form' && wizard.status?.steps.fields) ||
              (step === 'review' && wizard.status?.status === 'ready');
            return (
              <span
                key={step}
                className={`rounded border px-2 py-0.5 text-sm ${
                  active
                    ? 'border-[var(--color-info)] text-[var(--color-surface-text)]'
                    : done
                      ? 'border-[var(--color-success)] text-[var(--color-surface-text-secondary)]'
                      : 'border-[var(--color-border)] text-[var(--color-surface-text-secondary)]'
                }`}
              >
                {index + 1}. {stepLabel(step)}
              </span>
            );
          })}
        </nav>
      </section>

      {wizard.errorKey && (
        <p role="alert" className="text-base text-[var(--color-error)]">
          {t(wizard.errorKey)}
        </p>
      )}

      {wizard.statusMessageKey && (
        <p role="status" className="text-base text-[var(--color-success)]">
          {t(wizard.statusMessageKey)}
        </p>
      )}

      {wizard.step === 'form' && wizard.status.requiresSetup && provisionFields.length > 0 && (
        <InstanceSetupSchemaForm
          fields={provisionFields}
          values={wizard.fieldValues}
          saving={wizard.saving}
          uploadingFieldKey={wizard.uploadingFieldKey}
          onChange={wizard.setFieldValue}
          onUploadFile={(fieldKey, file) => void wizard.uploadFile(fieldKey, file)}
          onNext={() => void wizard.saveFields()}
        />
      )}

      {wizard.step === 'review' && (
        <InstanceSetupReviewStep
          status={wizard.status}
          schema={wizard.schema}
          fieldValues={wizard.fieldValues}
          saving={wizard.saving}
          onBack={() => wizard.setStep('form')}
          onComplete={() => void wizard.completeSetup()}
          onGoToInstance={wizard.goToInstance}
        />
      )}

      {!wizard.status.requiresSetup && wizard.status.status !== 'ready' && wizard.step === 'review' && (
        <p className="text-sm text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.instanceSetup.notRequiredHint')}
        </p>
      )}
    </div>
  );
};
